import { useEffect, useState } from "react";
import api from "../lib/api";
import { AlertCircle, X, Megaphone } from "lucide-react";
import { formatDate, relTime } from "../lib/date";

export default function TelecallerNotices() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/notices").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNotice = async (n) => {
    setOpen(n);
    if (!n.is_read) {
      try { await api.post(`/notices/${n.id}/read`); } catch {}
      setItems((arr) => arr.map((x) => x.id === n.id ? { ...x, is_read: true } : x));
      // Bump sidebar badge
      window.dispatchEvent(new Event("notices-updated"));
    }
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-6">
        <div className="mb-1 text-[11px] uppercase tracking-widest text-zinc-500">Announcements</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-black">Notices</h1>
      </div>

      {items.length === 0 && (
        <div className="rounded-sm border border-zinc-200 bg-white p-8 text-center text-sm text-zinc-500">
          <Megaphone className="mx-auto mb-2 h-6 w-6 text-zinc-300"/>
          No notices yet.
        </div>
      )}

      <div className="space-y-3">
        {items.map(n => (
          <button key={n.id} data-testid={`notice-card-${n.id}`} onClick={() => openNotice(n)}
            className={`group w-full text-left rounded-sm border p-4 transition-colors ${
              n.is_read ? "border-zinc-200 bg-white" : "border-zinc-300 bg-white shadow-sm"
            } hover:border-zinc-400`}>
            <div className="flex items-start gap-3">
              {!n.is_read && <span data-testid={`notice-unread-dot-${n.id}`} className="mt-2 h-2 w-2 shrink-0 rounded-full bg-emerald-500"/>}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  {n.priority === "important" && (
                    <span className="inline-flex items-center gap-1 rounded-sm bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
                      <AlertCircle className="h-3 w-3"/>Important
                    </span>
                  )}
                  <span className="font-heading text-base font-semibold text-black">{n.title}</span>
                  {!n.is_read && <span className="text-[10px] font-medium text-emerald-600 uppercase tracking-wider">New</span>}
                </div>
                <div className="text-sm text-zinc-600 line-clamp-2">{n.message}</div>
                <div className="mt-1 text-[11px] text-zinc-400" title={formatDate(n.created_at)}>Published {relTime(n.created_at)}</div>
              </div>
              {n.image && <img src={n.image} alt="" className="hidden sm:block h-16 w-16 rounded-sm object-cover border border-zinc-200"/>}
            </div>
          </button>
        ))}
      </div>

      {open && <NoticeModal notice={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function NoticeModal({ notice, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/50 p-3 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-sm border border-zinc-200 bg-white p-5 sm:p-6 my-8">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              {notice.priority === "important" && (
                <span className="inline-flex items-center gap-1 rounded-sm bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
                  <AlertCircle className="h-3 w-3"/>Important
                </span>
              )}
              <span className="text-[11px] uppercase tracking-widest text-zinc-500">Notice</span>
            </div>
            <h3 className="font-heading text-xl sm:text-2xl font-bold text-black">{notice.title}</h3>
            <div className="mt-1 text-[11px] text-zinc-500">Published {formatDate(notice.created_at)}</div>
          </div>
          <button data-testid="notice-modal-close" onClick={onClose} className="rounded-sm p-1 hover:bg-zinc-100"><X className="h-5 w-5"/></button>
        </div>
        <div className="mb-4 whitespace-pre-wrap text-sm text-zinc-800 leading-relaxed">{notice.message}</div>
        {notice.image && (
          <img src={notice.image} alt="notice attachment" className="w-full max-h-[60vh] object-contain rounded-sm border border-zinc-200" />
        )}
      </div>
    </div>
  );
}
